/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import styles from "./task-card.module.scss";
import primaryArrow from "assets/images/v2/Primary_Arrow.svg";
import secondaryArrow from "assets/images/v2/Secondary_Arrow.svg";
import { getCurrentUserId, getNhDealApplicationId, setProfileActiveTab, setSelectedTaskId } from "features/authSlice";
import { NeedStatus, NeedItem, useGetNeedsQuery } from "pages/dashboard/home/dashboardService";
import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Spinner } from "../spinner/spinner";
import { AdditionalDocumentationCondition } from "utilities/Constant";
import useDesktopChecker from "hooks/useDesktopChecker";
import { isNonEmptyString } from "utilities/string";
import Pill from "../pill/pill";
import { useESignFSTracking } from "hooks/useESignFSTracking";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { shouldDisplayNafInsuranceTaskCta } from "utilities/needsHelper";
import NafInsuranceChip from "../naf-insurance-chip/NafInsuranceChip";

type TaskCardProps = {
  readonly onNeedsLoaded?: (hasNeeds: boolean) => void;
  readonly skipNeedsQuery?: boolean;
};


export default function TaskCard({ onNeedsLoaded, skipNeedsQuery = false }: TaskCardProps) {
  const [needsList, setNeedsList] = useState<NeedItem[]>([]);
  const [hasServiceErrors, setHasServiceErrors] = useState(false);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dispatch = useDispatch();
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const isDesktop = useDesktopChecker();
  const totalTaskServices = 3;

  const shouldSkipQuery = !nhDealApplicationId || !userId || skipNeedsQuery;

  const {
    data: needItems,
    isLoading: isNeedsListLoading,
    isFetching,
    isUninitialized,
    error,
  } = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      skip: shouldSkipQuery,
      refetchOnMountOrArgChange: true,
    },
  );

  useESignFSTracking(needItems, { trackCreated: true });

  const isDataLoading = shouldSkipQuery || isUninitialized || isNeedsListLoading || isFetching;
  
  useEffect(() => {
    const uniqueErrorTypes = new Set(
      (needItems ?? [])
        .filter(item => !!item.taskLoadErrorType)
        .map(item => item.taskLoadErrorType)
    );
    const allServicesFailed = uniqueErrorTypes.size === totalTaskServices;
    setHasServiceErrors(allServicesFailed);

    if (allServicesFailed) {
      setNeedsList([]);
      onNeedsLoaded?.(false);
      return;
    }

    const filteredList = (needItems ?? []).filter(
      (item) =>
        item.needStatus?.toLowerCase() != NeedStatus.Satisfied.toLowerCase() &&
        item.needStatus?.toLowerCase() != NeedStatus.ReviewNeeded.toLowerCase() &&
        item.title !== AdditionalDocumentationCondition &&
        !item.taskLoadErrorType
    );

    setNeedsList(filteredList);
    onNeedsLoaded?.(filteredList.length > 0);

    if (filteredList.length > 0) {
      setSelectedTaskId(filteredList[0].id);
    }
  }, [needItems, onNeedsLoaded]);

  const handleViewAllClick = () => {
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  const handleTaskClick = (task: NeedItem) => {
    dispatch(setSelectedTaskId(task.id));
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  return (
    <div className={styles.taskCard}>
      <div className={styles.heading}>
        {isDesktop && <h5 className="heading-medium">My Tasks</h5>}
        {!isDesktop && <h6 className="heading-medium">My Tasks</h6>}
      </div>
      <div className={styles.content2}>
        {error && <div className={styles.errorMessage}>We are having issues loading your tasks. Try again later.</div>}
        {isDataLoading && (
          <div className={styles.spinnerContainer}>
            <Spinner isApi />
          </div>
        )}
        
        {hasServiceErrors && !isDataLoading && !error && (
          <div className={styles.errorMessage}>
            We are having issues loading your tasks. Try again later.
          </div>
        )}
        
        <div className={styles.taskContent1}>
          {needsList
            .filter((item) => item.needStatus?.toLowerCase() != NeedStatus.Completed.toLowerCase())
            .slice(0, 3)
            .map((task) => (
              <button
                className={styles.taskContentParent}
                key={task.title}
                onClick={() => handleTaskClick(task)}
                aria-label={`Select task: ${task.title}`}
              >
                <div className={styles.frameChild} />
                <div className={styles.taskContent2}>
                  <div className={styles.rectangleParent}>
                    <div className={styles.taskDetails}>
                      <div className={styles.taskTile}>{task.title}</div>
                      {isNonEmptyString(task.assignedTo) && <Pill text={task.assignedTo || ""} variant="assigned" />}
                      {shouldDisplayNafInsuranceTaskCta(task, featureFlag?.insurancePurchaseBannerEnabled) && <NafInsuranceChip />}
                      {(task.isNewComment === true|| !!task.note?.trim()) && <Pill text="New Comments" variant="comments" />}
                    </div>
                  </div>
                </div>
                <img className={styles.primaryIcon} alt="Chevron right icon" src={secondaryArrow} />
              </button>
            ))}

          {needsList.length === 0 && !isDataLoading && !error && !hasServiceErrors && (
            <div className={styles.noTasksMessageContainer}>
              <div className={styles.noTasksMessage}>You’re all caught up!</div>
            </div>
          )}

          {needsList.length >= 3 && (
            <div className={styles.taskContentInner}>
              <button className={styles.viewAllParent} onClick={handleViewAllClick} aria-label="View all tasks">
                <div className={styles.viewDetails}>View all</div>
                <img className={styles.chevronRightIcon} alt="Chevron right icon" src={primaryArrow} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
